import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { XMarkIcon, PencilIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { getTournaments } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { useSponsorshipTiers } from '../hooks/useSponsorshipTiers';
import PartnerFinanceTab from './PartnerFinanceTab';

interface FormattedPartner {
  id: number;
  company: string;
  logo: string;
  contactPerson: string;
  email: string;
  phone: string;
  website?: string;
  tier: string;
  status: string;
  contribution: string;
  contributionAmount?: number;
  startDate: string;
  endDate: string;
  benefits: string[];
  description?: string;
  stats?: string[];
  image?: string;
  tournamentId?: string;
}

interface PartnerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  partner: FormattedPartner | null;
  onUpdate?: (partner: FormattedPartner) => void;
  onDelete?: (partner: FormattedPartner) => void;
  onDuplicate?: () => void;
}

export default function PartnerDetailsModal({
  isOpen,
  onClose,
  partner,
  onUpdate,
  onDelete,
  onDuplicate
}: PartnerDetailsModalProps) {
  const { tiers: allTiers } = useSponsorshipTiers();
  const [isEditing, setIsEditing] = useState(false);
  const [editedPartner, setEditedPartner] = useState<FormattedPartner | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [allTournaments, setAllTournaments] = useState<{ id: string; name: string; status: string }[]>([]);
  const [realPartnerId, setRealPartnerId] = useState<string | null>(null);

  useEffect(() => {
    if (partner) {
      setEditedPartner({
        ...partner,
        contributionAmount: partner.contributionAmount ?? 0
      });
    }
  }, [partner]);

  useEffect(() => {
    getTournaments().then(({ data }) => {
      if (data) setAllTournaments(data.map(t => ({ id: t.id, name: t.name, status: t.status })));
    });
  }, []);

  useEffect(() => {
    if (!partner) { setRealPartnerId(null); return; }
    supabase
      .from('partners')
      .select('id')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        const found = data?.find(p => {
          const numericId = parseInt(p.id.replace(/-/g, '').substring(0, 8), 16);
          return numericId === partner.id;
        });
        setRealPartnerId(found?.id ?? null);
      });
  }, [partner]);

  if (!partner || !editedPartner) {
    return null;
  }

  const tierBenefits: Record<string, string[]> = Object.fromEntries(
    allTiers.map(t => [t.name, t.features])
  );

  const getDefaultContributionForTier = (tier: string): number => {
    const found = allTiers.find(t => t.name === tier);
    return found?.min_contribution ?? 0;
  };

  const handleSave = async () => {
    if (!editedPartner) return;
    
    try {
      setIsSubmitting(true);
      
      const contributionAmount = editedPartner.contributionAmount || getDefaultContributionForTier(editedPartner.tier);
      
      const { data: partners, error: fetchError } = await supabase
        .from('partners')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      const originalPartner = partners?.find(p => {
        const numericId = parseInt(p.id.replace(/-/g, '').substring(0, 8), 16);
        return numericId === editedPartner.id;
      });
      
      if (!originalPartner) {
        throw new Error('Partner not found');
      }
      
      const updateData = {
        company_name: editedPartner.company,
        logo_url: editedPartner.logo,
        contact_person: editedPartner.contactPerson,
        email: editedPartner.email,
        phone: editedPartner.phone,
        website: editedPartner.website,
        tier: editedPartner.tier,
        status: editedPartner.status.toLowerCase(),
        contribution: contributionAmount,
        start_date: editedPartner.startDate,
        end_date: editedPartner.endDate,
        benefits: editedPartner.benefits,
        description: editedPartner.description,
        stats: editedPartner.stats,
        image_url: editedPartner.image,
        tournament_id: editedPartner.tournamentId ?? null,
      };
      
      const { error: updateError } = await supabase
        .from('partners')
        .update(updateData)
        .eq('id', originalPartner.id);
      
      if (updateError) throw updateError;
      
      if (onUpdate) {
        const formattedPartner = {
          ...editedPartner,
          contribution: `₦${contributionAmount}` // Display exact value
        };
        onUpdate(formattedPartner);
      }
      
      toast.success('Partner updated successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating partner:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update partner');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!partner) return;
    
    try {
      setIsDeleting(true);
      
      const { data: partners, error: fetchError } = await supabase
        .from('partners')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      const originalPartner = partners?.find(p => {
        const numericId = parseInt(p.id.replace(/-/g, '').substring(0, 8), 16);
        return numericId === partner.id;
      });
      
      if (!originalPartner) {
        throw new Error('Partner not found');
      }

      // First delete all related records in partner_status_history
      const { error: historyDeleteError } = await supabase
        .from('partner_status_history')
        .delete()
        .eq('partner_id', originalPartner.id);

      if (historyDeleteError) throw historyDeleteError;

      // Then delete any financial transactions related to the partner
      const { error: transactionsDeleteError } = await supabase
        .from('financial_transactions')
        .delete()
        .eq('partner_id', originalPartner.id);

      if (transactionsDeleteError) throw transactionsDeleteError;
      
      // Finally delete the partner
      const { error: deleteError } = await supabase
        .from('partners')
        .delete()
        .eq('id', originalPartner.id);
      
      if (deleteError) throw deleteError;
      
      toast.success('Partner deleted successfully');
      
      // Call the onDelete callback if provided
      if (onDelete) {
        onDelete(partner);
      }
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete partner');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDuplicate = async () => {
    if (!partner) return;
    try {
      setIsDuplicating(true);
      const { data: partners, error: fetchError } = await supabase
        .from('partners')
        .select('*')
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      const original = partners?.find(p => {
        const numericId = parseInt(p.id.replace(/-/g, '').substring(0, 8), 16);
        return numericId === partner.id;
      });
      if (!original) throw new Error('Partner not found');
      const { id: _id, created_at: _ca, updated_at: _ua, ...rest } = original;
      const { error: insertError } = await supabase
        .from('partners')
        .insert({
          ...rest,
          company_name: `Copy of ${original.company_name}`,
          status: 'pending',
        });
      if (insertError) throw insertError;
      toast.success(`Duplicated ${partner.company} successfully`);
      onDuplicate?.();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to duplicate partner');
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleCancel = () => {
    setEditedPartner({ ...partner });
    setIsEditing(false);
  };

  const handleInputChange = (field: keyof FormattedPartner, value: any) => {
    if (editedPartner) {
      if (field === 'tier') {
        const defaultContribution = getDefaultContributionForTier(value);
        setEditedPartner({ 
          ...editedPartner, 
          [field]: value,
          contributionAmount: defaultContribution,
          contribution: `₦${defaultContribution}`
        });
      } else if (field === 'contribution') {
        // Remove any non-numeric characters except decimal point
        const numericValue = value.replace(/[^0-9.]/g, '');
        const amount = parseFloat(numericValue);
        
        setEditedPartner({ 
          ...editedPartner, 
          contribution: `₦${numericValue}`,
          contributionAmount: amount
        });
      } else {
        setEditedPartner({ ...editedPartner, [field]: value });
      }
    }
  };

  const renderField = (label: string, field: keyof FormattedPartner, type: string = 'text') => {
    return (
      <div>
        <label className="block text-sm text-gray-500">{label}</label>
        {isEditing ? (
          field === 'contribution' ? (
            <div className="relative mt-1">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">₦</span>
              <input
                type="number"
                step="any"
                value={editedPartner.contributionAmount || ''}
                onChange={(e) => handleInputChange(field, e.target.value)}
                className="pl-8 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                placeholder="Enter exact amount"
              />
            </div>
          ) : (
            <input
              type={type}
              value={editedPartner[field] as string}
              onChange={(e) => handleInputChange(field, e.target.value)}
              className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
            />
          )
        ) : (
          field === 'website' ? (
            <a 
              href={editedPartner[field] as string} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-golf-orange hover:text-golf-green font-medium"
            >
              {editedPartner[field]}
            </a>
          ) : (
            <p className="text-golf-gray font-medium">{editedPartner[field]}</p>
          )
        )}
      </div>
    );
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg md:rounded-2xl bg-white text-left shadow-xl transition-all w-full sm:my-8 sm:max-w-4xl mx-4 sm:mx-auto">
                {showDeleteConfirm && (
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                      <h3 className="text-xl font-bold text-golf-gray mb-4">Confirm Deletion</h3>
                      <p className="text-gray-600 mb-6">
                        Are you sure you want to delete {partner.company}? This action cannot be undone.
                      </p>
                      <div className="flex flex-col sm:flex-row justify-end gap-3">
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          className="w-full sm:w-auto px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-golf-green px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <Dialog.Title className="text-xl font-display font-bold text-white">
                      Partner Details
                    </Dialog.Title>
                    <div className="flex items-center space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSave}
                            disabled={isSubmitting}
                            className="px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
                          >
                            {isSubmitting ? 'Saving...' : 'Save'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setIsEditing(true)}
                            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                            title="Edit partner"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={handleDuplicate}
                            disabled={isDuplicating}
                            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Duplicate partner"
                          >
                            <DocumentDuplicateIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={isDeleting}
                            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                            title="Delete partner"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={onClose}
                            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                          >
                            <XMarkIcon className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <Tab.Group>
                  <Tab.List className="flex space-x-1 border-b border-gray-200 px-4 sm:px-6 overflow-x-auto">
                    {['Overview', 'Tier', 'Finance', 'Content'].map((category) => (
                      <Tab
                        key={category}
                        className={({ selected }) =>
                          `py-3 sm:py-4 px-4 sm:px-6 text-sm font-medium whitespace-nowrap focus:outline-none ${
                            selected
                              ? 'text-golf-orange border-b-2 border-golf-orange'
                              : 'text-gray-500 hover:text-golf-gray'
                          }`
                        }
                      >
                        {category}
                      </Tab>
                    ))}
                  </Tab.List>

                  <Tab.Panels className="p-4 sm:p-6">
                    <Tab.Panel>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        <div>
                          <h3 className="text-lg font-semibold text-golf-gray mb-4">Partner Information</h3>
                          <div className="space-y-4">
                            {renderField('Company', 'company')}
                            {renderField('Contact Person', 'contactPerson')}
                            {renderField('Email', 'email', 'email')}
                            {renderField('Phone', 'phone', 'tel')}
                            {renderField('Website', 'website', 'url')}
                          </div>
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-golf-gray mb-4">Partnership Details</h3>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm text-gray-500">Tournament</label>
                              {isEditing ? (
                                <select
                                  value={editedPartner.tournamentId ?? ''}
                                  onChange={(e) => handleInputChange('tournamentId', e.target.value || undefined)}
                                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                                >
                                  <option value="">— None —</option>
                                  {['upcoming', 'ongoing', 'completed'].map(status => {
                                    const group = allTournaments.filter(t => t.status === status);
                                    if (!group.length) return null;
                                    return (
                                      <optgroup key={status} label={status.charAt(0).toUpperCase() + status.slice(1)}>
                                        {group.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                      </optgroup>
                                    );
                                  })}
                                </select>
                              ) : (
                                <p className="text-golf-gray font-medium">
                                  {allTournaments.find(t => t.id === editedPartner.tournamentId)?.name ?? '—'}
                                </p>
                              )}
                            </div>
                          <div>
                              <label className="block text-sm text-gray-500">Status</label>
                              {isEditing ? (
                                <select
                                  value={editedPartner.status}
                                  onChange={(e) => handleInputChange('status', e.target.value)}
                                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                                >
                                  <option value="Active">Active</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Expired">Expired</option>
                                </select>
                              ) : (
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  editedPartner.status === 'Active'
                                    ? 'bg-green-100 text-green-800'
                                    : editedPartner.status === 'Pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {editedPartner.status}
                                </span>
                              )}
                            </div>
                            {renderField('Contribution', 'contribution')}
                            {renderField('Start Date', 'startDate', 'date')}
                            {renderField('End Date', 'endDate', 'date')}
                            <div>
                              <label className="block text-sm text-gray-500">Benefits</label>
                              {isEditing ? (
                                <div className="mt-1 space-y-2">
                                  {editedPartner.benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={benefit}
                                        onChange={(e) => {
                                          const newBenefits = [...editedPartner.benefits];
                                          newBenefits[index] = e.target.value;
                                          handleInputChange('benefits', newBenefits);
                                        }}
                                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                                      />
                                      <button
                                        onClick={() => {
                                          const newBenefits = editedPartner.benefits.filter((_, i) => i !== index);
                                          handleInputChange('benefits', newBenefits);
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    onClick={() => {
                                      const newBenefits = [...editedPartner.benefits, ''];
                                      handleInputChange('benefits', newBenefits);
                                    }}
                                    className="text-golf-orange hover:text-golf-green"
                                  >
                                    Add Benefit
                                  </button>
                                </div>
                              ) : (
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {editedPartner.benefits.map((benefit, index) => (
                                    <span
                                      key={index}
                                      className="px-2 py-1 text-xs bg-golf-orange/10 text-golf-orange rounded-full"
                                    >
                                      {benefit}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Tab.Panel>

                    <Tab.Panel>
                      <div className="space-y-6">
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-golf-gray">
                              {editedPartner.tier} Benefits
                            </h3>
                            {isEditing && (
                              <select
                                value={editedPartner.tier}
                                onChange={(e) => handleInputChange('tier', e.target.value)}
                                className="rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                              >
                                {allTiers.map(t => (
                                  <option key={t.id} value={t.name}>{t.name}</option>
                                ))}
                              </select>
                            )}
                          </div>
                          <div className="bg-golf-sand/30 rounded-xl p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {tierBenefits[editedPartner.tier as keyof typeof tierBenefits]?.map((benefit, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-3"
                                >
                                  <svg
                                    className="w-5 h-5 text-golf-orange flex-shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                  <span className="text-golf-gray">{benefit}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-golf-gray mb-4">
                            Partnership Statistics
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {editedPartner.stats?.map((stat, index) => (
                              <div key={index}>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={stat}
                                    onChange={(e) => {
                                      const newStats = [...(editedPartner.stats || [])];
                                      newStats[index] = e.target.value;
                                      handleInputChange('stats', newStats);
                                    }}
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                                  />
                                ) : (
                                  <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                                    <p className="text-golf-gray font-medium">{stat}</p>
                                  </div>
                                )}
                              </div>
                            )) || []}
                            {isEditing && (
                              <div>
                                <button
                                  onClick={() => {
                                    const newStats = [...(editedPartner.stats || []), ''];
                                    handleInputChange('stats', newStats);
                                  }}
                                  className="w-full h-full min-h-[42px] border border-dashed border-gray-300 rounded-lg flex items-center justify-center text-golf-orange hover:text-golf-green hover:border-golf-orange"
                                >
                                  Add Stat
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Tab.Panel>

                    <Tab.Panel>
                      {realPartnerId ? (
                        <PartnerFinanceTab
                          partnerId={realPartnerId}
                          partnerCompany={editedPartner.company}
                          partnerTier={editedPartner.tier}
                          contributionAmount={editedPartner.contributionAmount || 0}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                          Loading financial data...
                        </div>
                      )}
                    </Tab.Panel>

                    <Tab.Panel>
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-semibold text-golf-gray mb-4">
                            About {editedPartner.company}
                          </h3>
                          {isEditing ? (
                            <textarea
                              value={editedPartner.description || ''}
                              onChange={(e) => handleInputChange('description', e.target.value)}
                              rows={4}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                              placeholder="Enter company description"
                            />
                          ) : (
                            <p className="text-gray-600">{editedPartner.description || 'No description available.'}</p>
                          )}
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-golf-gray mb-4">
                            Featured Content
                          </h3>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editedPartner.image || ''}
                              onChange={(e) => handleInputChange('image', e.target.value)}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                              placeholder="Image URL"
                            />
                          ) : (
                            editedPartner.image ? (
                              <div className="relative aspect-video rounded-xl overflow-hidden">
                                <img
                                  src={editedPartner.image}
                                  alt={editedPartner.company}
                                  className="absolute inset-0 w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="bg-gray-100 aspect-video rounded-xl flex items-center justify-center">
                                <p className="text-gray-500">No image available</p>
                              </div>
                            )
                          )}
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-golf-gray mb-4">
                            Brand Assets
                          </h3>
                          <div className="bg-white rounded-xl border border-gray-200 p-6">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editedPartner.logo}
                                onChange={(e) => handleInputChange('logo', e.target.value)}
                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                                placeholder="Logo URL"
                              />
                            ) : (
                              <div className="flex items-center justify-center">
                                <img
                                  src={editedPartner.logo}
                                  alt={`${editedPartner.company} logo`}
                                  className="h-24 object-contain"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}